import { useState } from "react";
import { Loader2, Save } from "lucide-react";
import { STATUSES } from "@/lib/statuses";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

export const EMPTY_SHIPMENT = {
  sender_name: "", recipient_name: "", recipient_phone: "", recipient_email: "",
  origin_city: "", origin_address: "", destination_city: "", destination_address: "",
  pickup_date: new Date().toISOString().slice(0, 10), expected_delivery: "", packages: 1, weight_kg: "",
  goods_type: "", internal_notes: "", notify_consent: false, status: "REGISTRATA",
};

const Section = ({ title, children }) => (
  <fieldset className="bg-white border border-slate-200 p-5 sm:p-6">
    <legend className="font-display text-lg font-bold uppercase tracking-tight text-slate-900 px-1">{title}</legend>
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-2">{children}</div>
  </fieldset>
);

const F = ({ label, id, full, children }) => (
  <div className={full ? "sm:col-span-2" : ""}>
    <Label htmlFor={id} className="text-xs font-semibold uppercase tracking-wider text-slate-500">{label}</Label>
    <div className="mt-1.5">{children}</div>
  </div>
);

export const ShipmentForm = ({ initial, onSubmit, submitting, mode = "create" }) => {
  const [f, setF] = useState({ ...EMPTY_SHIPMENT, ...initial, recipient_email: initial?.recipient_email || "", expected_delivery: initial?.expected_delivery || "" });
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const inp = (k, props = {}) => <Input id={k} value={f[k] ?? ""} onChange={set(k)} className="rounded-none" data-testid={`form-${k.replace(/_/g, "-")}`} {...props} />;

  const submit = (e) => {
    e.preventDefault();
    const payload = { ...f, packages: Number(f.packages) || 1, weight_kg: Number(f.weight_kg) || 0, recipient_email: f.recipient_email || null, expected_delivery: f.expected_delivery || null };
    if (mode !== "create") delete payload.status;
    onSubmit(payload);
  };

  return (
    <form onSubmit={submit} className="space-y-6" data-testid="shipment-form">
      <Section title="Mittente e destinatario">
        <F label="Nome del mittente *" id="sender_name">{inp("sender_name", { required: true })}</F>
        <F label="Nome del destinatario *" id="recipient_name">{inp("recipient_name", { required: true })}</F>
        <F label="Telefono destinatario" id="recipient_phone">{inp("recipient_phone", { type: "tel", placeholder: "+39 ..." })}</F>
        <F label="Email destinatario" id="recipient_email">{inp("recipient_email", { type: "email" })}</F>
        <div className="sm:col-span-2 flex items-start gap-3 bg-slate-50 border border-slate-200 p-3">
          <Checkbox id="notify_consent" checked={f.notify_consent} onCheckedChange={(v) => setF({ ...f, notify_consent: !!v })} data-testid="form-notify-consent" />
          <Label htmlFor="notify_consent" className="text-sm font-normal text-slate-600 leading-snug">Il destinatario ha acconsentito a ricevere notifiche automatiche sullo stato della spedizione (email, e WhatsApp/SMS quando attivati).</Label>
        </div>
      </Section>
      <Section title="Percorso">
        <F label="Città di partenza *" id="origin_city">{inp("origin_city", { required: true })}</F>
        <F label="Indirizzo di partenza" id="origin_address">{inp("origin_address")}</F>
        <F label="Città di destinazione *" id="destination_city">{inp("destination_city", { required: true })}</F>
        <F label="Indirizzo di destinazione" id="destination_address">{inp("destination_address")}</F>
      </Section>
      <Section title="Date e merce">
        <F label="Data di affidamento al corriere *" id="pickup_date">{inp("pickup_date", { type: "date", required: true })}</F>
        <F label="Data di consegna prevista" id="expected_delivery">{inp("expected_delivery", { type: "date", min: f.pickup_date })}</F>
        <F label="Numero di colli *" id="packages">{inp("packages", { type: "number", min: 1, required: true })}</F>
        <F label="Peso (kg)" id="weight_kg">{inp("weight_kg", { type: "number", min: 0, step: "0.01" })}</F>
        <F label="Tipologia di merce" id="goods_type" full>{inp("goods_type", { placeholder: "Es. pallet, colli, materiale edile" })}</F>
      </Section>
      <Section title="Stato e note">
        {mode === "create" && (
          <F label="Stato iniziale *" id="status">
            <Select value={f.status} onValueChange={(v) => setF({ ...f, status: v })}>
              <SelectTrigger className="rounded-none" data-testid="form-status-select"><SelectValue /></SelectTrigger>
              <SelectContent>{STATUSES.map((s) => <SelectItem key={s.key} value={s.key}>{s.label}</SelectItem>)}</SelectContent>
            </Select>
          </F>
        )}
        <F label="Note interne (non visibili al cliente)" id="internal_notes" full>
          <Textarea id="internal_notes" rows={3} value={f.internal_notes} onChange={set("internal_notes")} className="rounded-none" data-testid="form-internal-notes" />
        </F>
      </Section>
      <Button type="submit" disabled={submitting} className="rounded-none bg-[#E50914] hover:bg-[#C10710] h-12 px-8 font-display text-base uppercase tracking-wider" data-testid="shipment-form-submit">
        {submitting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
        {mode === "create" ? "Salva e genera codice" : "Salva modifiche"}
      </Button>
    </form>
  );
};
